import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import { ZoomableCalendar } from '../components/Calendar/ZoomableCalendar';
import { TimeSlotLeaderboard } from '../components/Calendar/TimeSlotLeaderboard';
import { RoomTabs } from '../components/Calendar/RoomTabs';
import { Header } from '../components/Layout/Header';
import { ParticipantsCard } from '../components/Room/ParticipantsCard';
import { Footer } from '../components/Layout/Footer';
import { RoomBackground } from '../components/Room/RoomBackground';
import { ServiceUnavailable } from '../components/UI/ServiceUnavailable';
import { apiService, generateBrowserFingerprint } from '../services/api';
import { Availability, TimeSlot } from '../types';
import { isConnectionError, isRateLimited } from '../utils/apiError';
import { persistSlotUpdates } from '../utils/persistSlots';
import { normalizeAvailabilityForViewer } from '../utils/availabilityMap';
import { getMissingHours, useLeaderboard } from '../hooks/useLeaderboard';
import { getTelegramDisplayName } from '../hooks/useTelegramWebApp';
import { shareRoomLink } from '../utils/shareRoom';
import {
  RoomTab,
  decideInitialRoomTab,
  readStoredRoomTab,
  writeStoredRoomTab,
} from '../utils/roomTabs';

export function Room() {
  const { t } = useTranslation();
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const [participantName, setParticipantName] = useState<string | null>(null);
  const [localAvailability, setLocalAvailability] = useState<Availability[]>([]);
  const [browserFingerprint] = useState(() => generateBrowserFingerprint());
  const [tab, setTab] = useState<RoomTab>('mark');
  const [tabInitialized, setTabInitialized] = useState(false);
  const [minDuration, setMinDuration] = useState(1);
  const [isCreatingRoom, setIsCreatingRoom] = useState(false);
  const [joinError, setJoinError] = useState<unknown>(null);
  const [createRoomError, setCreateRoomError] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!notice) return;
    const id = window.setTimeout(() => setNotice(null), 4000);
    return () => window.clearTimeout(id);
  }, [notice]);

  useEffect(() => {
    setTab('mark');
    setTabInitialized(false);
    setParticipantName(null);
    setLocalAvailability([]);
    setJoinError(null);
    setCreateRoomError(null);
    setNotice(null);
  }, [roomId]);

  const { data: roomData, isLoading, error, refetch } = useQuery({
    queryKey: ['room', roomId],
    queryFn: async () => {
      if (!roomId) return Promise.reject('No room ID');
      
      const rawData = await apiService.getRoom(roomId);
      
      const localAvailability = normalizeAvailabilityForViewer(rawData.availability);
      
      return {
        ...rawData,
        availability: localAvailability,
      };
    },
    enabled: !!roomId,
    refetchInterval: query => (query.state.status === 'error' ? false : 5000),
  });

  useEffect(() => {
    if (roomData) {
      setLocalAvailability(roomData.availability);
    }
  }, [roomData]);

  const { slots: bestSlots } = useLeaderboard({
    roomId,
    minDuration,
    availability: localAvailability,
  });

  const hasMarkedSlots = useMemo(
    () => localAvailability.some(a => a.available),
    [localAvailability]
  );

  useEffect(() => {
    if (tabInitialized || !participantName || !roomId) return;
    const stored = readStoredRoomTab(roomId);
    setTab(decideInitialRoomTab(localAvailability, participantName, stored));
    setTabInitialized(true);
  }, [tabInitialized, participantName, localAvailability, roomId]);

  const handleTabChange = (next: RoomTab) => {
    setTab(next);
    if (roomId) writeStoredRoomTab(roomId, next);
  };

  useEffect(() => {
    const joinRoom = async () => {
      if (!roomId || participantName || !roomData) return;

      try {
        const response = await apiService.joinRoom(roomId, browserFingerprint);
        setParticipantName(response.assigned_name);
        setJoinError(null);
      } catch (error) {
        console.error('Failed to join room:', error);
        setJoinError(error);
      }
    };

    joinRoom();
  }, [roomId, browserFingerprint, participantName, roomData]);

  useEffect(() => {
    if (!roomId || !participantName) return;

    const desiredName = getTelegramDisplayName();
    if (!desiredName) return;

    const key = `tg_name_applied:${roomId}`;
    if (localStorage.getItem(key)) return;

    if (desiredName === participantName) {
      localStorage.setItem(key, '1');
      return;
    }

    const oldName = participantName;
    const applyName = async () => {
      try {
        const result = await apiService.renameParticipant(
          roomId,
          oldName,
          desiredName,
          browserFingerprint
        );

        if (result.success) {
          setLocalAvailability((prev) =>
            prev.map((avail) =>
              avail.participant === oldName ? { ...avail, participant: desiredName } : avail
            )
          );
          setParticipantName(desiredName);
          refetch();
          localStorage.setItem(key, '1');
        }
      } catch (error) {
        console.error('Failed to rename participant with Telegram name:', error);
      }
    };

    applyName();
  }, [roomId, participantName, browserFingerprint, refetch]);

  const handleCreateNewRoom = async () => {
    if (isCreatingRoom) return;
    setIsCreatingRoom(true);
    setCreateRoomError(null);
    try {
      const response = await apiService.createRoom();
      if (!response.room_id) {
        throw new Error('Missing room id');
      }
      window.location.href = `/room/${response.room_id}`;
    } catch (error) {
      console.error('Failed to create new room:', error);
      setCreateRoomError(error);
      setIsCreatingRoom(false);
    }
  };

  const handleRetryJoin = () => {
    setJoinError(null);
    setParticipantName(null);
  };

  const handleRename = async (newName: string) => {
    if (!roomId || !participantName || newName === participantName) return;
    try {
      await apiService.renameParticipant(roomId, participantName, newName, browserFingerprint);
      const updatedAvailability = localAvailability.map(avail =>
        avail.participant === participantName ? { ...avail, participant: newName } : avail
      );
      setLocalAvailability(updatedAvailability);
      setParticipantName(newName);
      refetch();
    } catch (error) {
      console.error('Failed to rename participant:', error);
      alert(t('room.failedToRename'));
    }
  };

  const handleInvite = async () => {
    if (!roomId) return;
    await shareRoomLink({
      roomId,
      title: t('share.shareTitle'),
      text: t('share.shareText'),
    });
  };

  const handleSlotSelect = async (slot: TimeSlot) => {
    if (!roomId || !participantName) return;

    // Find missing hours that need to be selected
    const missingHours = getMissingHours(slot, participantName, localAvailability);

    if (missingHours.length === 0) {
      return;
    }

    try {
      const { failed, rateLimited, participantName: resolvedName } = await persistSlotUpdates({
        roomId,
        participantName,
        browserFingerprint,
        slots: missingHours,
        available: true,
        onNameReassigned: setParticipantName,
      });
      if (rateLimited) {
        setNotice(t('room.rateLimited'));
      }
      if (failed.length) {
        throw new Error('slot update failed');
      }

      const name = resolvedName;
      const newAvailabilities = missingHours.map(({ day, hour }) => ({
        room_id: roomId,
        participant: name,
        day,
        hour,
        available: true,
        updated_at: Date.now() / 1000,
      }));

      setLocalAvailability([...localAvailability, ...newAvailabilities]);
      refetch();
    } catch (error) {
      console.error('Failed to select slot:', error);
      if (isRateLimited(error)) {
        setNotice(t('room.rateLimited'));
      } else {
        alert(t('room.failedToSelectSlot'));
      }
    }
  };

  if (!roomId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('room.invalidRoom')}</h1>
          <p className="text-gray-600 mb-4">{t('room.invalidRoomDescription')}</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            {t('room.goHome')}
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="room-page min-h-screen">
        <RoomBackground />
        <div className="room-page-content flex items-center justify-center py-20">
          <div className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-room-accent" />
            <p className="text-room-text-dim">{t('room.loadingRoom')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    if (isConnectionError(error)) {
      return (
        <ServiceUnavailable
          onRetry={() => refetch()}
          onGoHome={() => navigate('/')}
        />
      );
    }

    return (
      <div className="room-page min-h-screen">
        <RoomBackground />
        <div className="room-page-content flex items-center justify-center py-20">
          <div className="text-center">
            <AlertCircle className="mx-auto mb-4 h-12 w-12 text-red-500" />
            <h1 className="mb-2 text-2xl font-bold text-room-text">{t('room.roomNotFound')}</h1>
            <p className="mb-4 text-room-text-dim">{t('room.roomNotFoundDescription')}</p>
            {createRoomError && isConnectionError(createRoomError) ? (
              <ServiceUnavailable
                compact
                onRetry={handleCreateNewRoom}
                retrying={isCreatingRoom}
              />
            ) : (
              <div className="space-x-4">
                <button
                  type="button"
                  onClick={() => refetch()}
                  className="rounded-lg bg-room-surface-2 px-4 py-2 text-room-text transition-colors hover:bg-room-surface-3"
                >
                  {t('room.tryAgain')}
                </button>
                <button
                  type="button"
                  onClick={handleCreateNewRoom}
                  disabled={isCreatingRoom}
                  className="rounded-lg bg-room-accent px-4 py-2 font-medium text-[#06231D] transition-colors hover:brightness-105 disabled:opacity-50"
                >
                  {isCreatingRoom ? t('home.creatingRoom') : t('room.createNewRoom')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!roomData || !participantName) {
    if (joinError && isConnectionError(joinError)) {
      return (
        <ServiceUnavailable
          onRetry={handleRetryJoin}
          onGoHome={() => navigate('/')}
        />
      );
    }

    return (
      <div className="room-page min-h-screen">
        <RoomBackground />
        <div className="room-page-content flex items-center justify-center py-20">
          <div className="text-center">
            <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2 border-room-accent" />
            <p className="text-room-text-dim">{t('room.joiningRoom')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="room-page min-w-0">
      <RoomBackground />
      <div className="room-page-content mx-auto w-full max-w-[820px] px-3 pb-10 pt-7">
        <Header
          roomId={roomId}
          roomName={roomData?.room?.name || t('calendar.defaultRoomName')}
          onCreateNewRoom={handleCreateNewRoom}
          roomMode
          onRoomNameUpdate={async (name: string) => {
            await apiService.updateRoomName(roomId, name, browserFingerprint);
            refetch();
          }}
        />

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="min-w-0">
          <RoomTabs
            tab={tab}
            onChange={handleTabChange}
            bestCount={bestSlots.length}
            showFilter={tab === 'best' && hasMarkedSlots}
            minDuration={minDuration}
            onMinDurationChange={setMinDuration}
          />

          {tab === 'mark' ? (
            <div className="mb-6 rounded-3xl border border-room-border bg-room-surface p-4 sm:p-5">
              <ZoomableCalendar
                roomId={roomId}
                participantName={participantName}
                availability={localAvailability}
                onRefresh={refetch}
                browserFingerprint={browserFingerprint}
                onNameReassigned={setParticipantName}
                onRateLimited={() => setNotice(t('room.rateLimited'))}
              />
            </div>
          ) : (
            <TimeSlotLeaderboard
              roomId={roomId}
              participantName={participantName}
              availability={localAvailability}
              participants={roomData.participants}
              minDuration={minDuration}
              onSlotSelect={handleSlotSelect}
            />
          )}

          <ParticipantsCard
            participants={roomData.participants}
            participantName={participantName}
            availability={localAvailability}
            onRename={handleRename}
            onInvite={handleInvite}
          />

          <Footer variant="room" />
        </motion.div>
      </div>
      {notice ? (
        <div className="fixed bottom-4 left-1/2 z-50 w-[min(90vw,24rem)] -translate-x-1/2 rounded-xl bg-room-text px-4 py-3 text-center text-sm text-room-bg shadow-lg">
          {notice}
        </div>
      ) : null}
    </div>
  );
}
